import { useEffect, useRef, useState } from "react";
import * as StateLib from "@/Utils/StateLib";
import cx from "classnames";
import { Checkbox, Divider, Flex, Radio, Text, Select } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { enumBranchPref } from "@coreapp/constants";
import { QOptimizePref } from "@coreapp/CoreApp/QOptimizePref";
import {
  RSBFooter,
  RSBPrimaryButton,
  RSBSecondaryButton
} from "@/appComponents/Navigation/RightSidebar/Utils/RSBFooter";

export const treeOptions: [string, number][] = [
  ["tree", enumBranchPref.tree],
  ["twocolumn", enumBranchPref.twocolumn],
  ["fourcolumn", enumBranchPref.fourcolumn]
];

type BranchPrefsCompositeProps = {
  pageOpt: QOptimizePref;
};

export const BranchPrefsComposite = ({ pageOpt: initialProp }: BranchPrefsCompositeProps) => {
  const [pageOpt, setPageOpt] = useState(() => initialProp.Clone());
  const [initialPageOpt, setInitialPageOpt] = useState(() => initialProp.Clone());

  const curAspect = pageOpt.GetAspectRatio() || 3;
  const editMode = StateLib.GetState() === 0;

  const isDirty =
    pageOpt.GetBranchPref() !== initialPageOpt.GetBranchPref() ||
    pageOpt.GetAllowSort() !== initialPageOpt.GetAllowSort() ||
    pageOpt.GetTreeSingles() !== initialPageOpt.GetTreeSingles() ||
    pageOpt.GetAspectRatio() !== initialPageOpt.GetAspectRatio();

  // Refs so the unmount cleanup always reads current values without stale closure.
  const isDirtyRef = useRef(isDirty);
  const initialPageOptRef = useRef(initialPageOpt);
  isDirtyRef.current = isDirty;
  initialPageOptRef.current = initialPageOpt;

  const doOptimizePage = (opt: QOptimizePref, param: string, persist = false) => {
    const wsMgr = StateLib.GetWSMgr();
    wsMgr?.DoOptimizePage(
      opt.GetAspectRatio(),
      opt.GetAllowSort(),
      opt.GetTreeSingles(),
      opt.GetBranchPref(),
      param,
      param === "OptimizeAll",
      true,
      true
    );
    if (persist) {
      const doc = StateLib.GetDoc();
      doc?.GetDocMgr()?.GetDocPrefs()?.SetPageOpt(opt);
    }
  };

  // Revert the chart preview on unmount (tab/mode switch) when there are unsaved changes.
  useEffect(() => {
    return () => {
      if (!isDirtyRef.current) return;
      doOptimizePage(initialPageOptRef.current, "", true);
    };
  }, []);

  const executeOptimization = (cmd: string | number) => {
    const next = pageOpt.Clone();
    let param = "";
    let persist = false;

    switch (cmd) {
      case enumBranchPref.tree:
      case enumBranchPref.twocolumn:
      case enumBranchPref.fourcolumn:
        next.SetBranchPref(cmd);
        break;
      case "optimizeAll":
        param = "OptimizeAll";
        setInitialPageOpt(next);
        persist = true;
        break;
      default:
        break;
    }

    setPageOpt(next);
    doOptimizePage(next, param, persist);
  };

  const previewAspectRatio = (ratio: number) => {
    const next = pageOpt.Clone();
    next.SetAspectRatio(ratio);
    setPageOpt(next);
    doOptimizePage(next, "", false);
  };

  const handleCancel = () => {
    const reverted = initialPageOpt.Clone();
    setPageOpt(reverted);
    doOptimizePage(reverted, "", true);
  };

  const aspectRatioOptions = [
    {
      label: appstr("Normal"),
      value: 3,
      clickHandler: () => previewAspectRatio(3)
    },
    {
      label: appstr("ppt1ps"),
      value: 5,
      clickHandler: () => previewAspectRatio(5)
    },
    {
      label: appstr("ppt2ps"),
      value: 7,
      clickHandler: () => previewAspectRatio(7)
    },
    {
      label: appstr("extrawidem"),
      value: 8,
      clickHandler: () => previewAspectRatio(8)
    }
  ];

  const baseClass = "ocn-sidebar-section-content";

  return (
    <Flex className={cx(baseClass)} height={"100%"} style={{ padding: 0 }}>
      <Flex
        className={"ocn-sidebar-section-container"}
        direction={"column"}
        flex={1}
        style={{ overflowY: "auto" }}
      >
        {editMode && (
          <Flex className={cx(baseClass)} gap={"12px"}>
            <Flex direction="column" gap="8px">
              <Text size={"small"} modifier={"bold"}>
                {appstr("prefstyle")}
              </Text>
              {treeOptions.map(([label, value]) => (
                <Radio
                  key={`tree-option-${value}`}
                  id={`tree-option-${value}`}
                  label={`${appstr(label)}`}
                  value={value}
                  checked={value === pageOpt.fBranchPref}
                  onChange={() => executeOptimization(value)}
                />
              ))}
            </Flex>
            <Flex direction={"column"} gap={"8px"}>
              <Checkbox
                id="resort-boxes"
                label={appstr("Resort boxes to save space")}
                size="medium"
                value="resort"
                checked={pageOpt.GetAllowSort()}
                onToggle={() => {
                  const next = pageOpt.Clone();
                  next.SetAllowSort(!next.GetAllowSort());
                  setPageOpt(next);
                }}
              />
              <Checkbox
                id="tree-style-singles"
                label={appstr("Use tree style for singles")}
                size="medium"
                value="treeSingle"
                checked={pageOpt.GetTreeSingles()}
                onToggle={() => {
                  const next = pageOpt.Clone();
                  next.SetTreeSingles(!next.GetTreeSingles());
                  setPageOpt(next);
                }}
              />
              <Divider />
              <Flex direction={"column"}>
                <Select
                  id={"rsb-aspect-select"}
                  inputOnly={false}
                  value={curAspect}
                  label={appstr("Set Aspect Ratio")}
                  selectSize={"small"}
                  options={aspectRatioOptions as any}
                  placeholder={appstr("Select")}
                />
              </Flex>
            </Flex>
          </Flex>
        )}
      </Flex>
      <RSBFooter>
        <RSBSecondaryButton onClick={handleCancel} disabled={!isDirty} />
        <RSBPrimaryButton disabled={!isDirty} onClick={() => executeOptimization("optimizeAll")}>
          {appstr("Apply to all subcharts")}
        </RSBPrimaryButton>
      </RSBFooter>
    </Flex>
  );
};
